/**
 * Apply Now — the applicant's own journey, from account to submitted.
 *
 *   /apply/:tenantId
 *
 * Register or sign in → choose a programme and intake → fill in the form →
 * upload documents → ask a pastor for a reference → submit. Everything saves
 * as they go, so they can come back later and pick up where they left off.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  GraduationCap, Loader2, CheckCircle2, Upload, ShieldCheck, LogIn, UserPlus, CreditCard, Smartphone,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'fwbc:applicant';

const pretty = (s) => String(s || '').replace(/_/g, ' ');

const call = async (path, { method = 'GET', body, token, form } = {}) => {
  const res = await fetch(`${API}/api${path}`, {
    method,
    headers: {
      ...(form ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: form || (body ? JSON.stringify(body) : undefined),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail
    : 'Something went wrong — please try again.');
  return data;
};

const Field = ({ id, label, children }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-slate-700 text-sm">{label}</Label>
    {children}
  </div>
);

const AuthCard = ({ tenantId, onIn }) => {
  const [mode, setMode] = useState('register');
  const [form, setForm] = useState({ email: '', password: '', first_name: '', last_name: '', phone: '' });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const go = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const path = mode === 'register' ? '/public/admissions/register' : '/public/admissions/login';
      const body = mode === 'register' ? { ...form, tenant_id: tenantId }
        : { tenant_id: tenantId, email: form.email, password: form.password };
      const out = await call(path, { method: 'POST', body });
      localStorage.setItem(TOKEN_KEY, out.access_token);
      onIn(out.access_token);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="max-w-md mx-auto" data-testid="apply-auth">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          {mode === 'register' ? <UserPlus className="h-5 w-5 text-indigo-600" />
            : <LogIn className="h-5 w-5 text-indigo-600" />}
          {mode === 'register' ? 'Start your application' : 'Continue your application'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={go} className="space-y-3">
          {mode === 'register' && (
            <div className="grid grid-cols-2 gap-3">
              <Field id="fn" label="First name">
                <Input id="fn" required value={form.first_name} onChange={set('first_name')}
                       data-testid="apply-first-name" />
              </Field>
              <Field id="ln" label="Last name">
                <Input id="ln" required value={form.last_name} onChange={set('last_name')}
                       data-testid="apply-last-name" />
              </Field>
            </div>
          )}
          <Field id="em" label="Email">
            <Input id="em" type="email" required value={form.email} onChange={set('email')}
                   data-testid="apply-email" />
          </Field>
          {mode === 'register' && (
            <Field id="ph" label="Phone">
              <Input id="ph" value={form.phone} onChange={set('phone')}
                     placeholder="+263 77 123 4567" data-testid="apply-phone" />
            </Field>
          )}
          <Field id="pw" label="Password">
            <Input id="pw" type="password" required minLength={8} value={form.password}
                   onChange={set('password')} data-testid="apply-password" />
          </Field>
          <Button type="submit" className="w-full" disabled={busy} data-testid="apply-submit-auth">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {mode === 'register' ? 'Create my account' : 'Sign in'}
          </Button>
          <button type="button" data-testid="apply-toggle-mode"
                  onClick={() => setMode(mode === 'register' ? 'login' : 'register')}
                  className="text-xs text-indigo-600 hover:underline w-full text-center">
            {mode === 'register' ? 'I already started — sign me in'
              : 'I am new here — create an account'}
          </button>
        </form>
      </CardContent>
    </Card>
  );
};

const ChooseProgramme = ({ tenantId, token, prospectus, onStarted }) => {
  const [choice, setChoice] = useState({
    programme_id: '', intake_id: '', applicant_type: 'local',
    study_mode: 'on_campus', attendance: 'full_time', country: 'ZW', source: '',
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setChoice({ ...choice, [k]: e.target.value });

  const start = async () => {
    if (!choice.programme_id) { toast.error('Choose a programme first'); return; }
    setBusy(true);
    try {
      onStarted(await call('/admissions/portal/applications',
        { method: 'POST', body: choice, token }));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const selectClass = 'h-10 w-full rounded-md border border-slate-200 px-2 text-sm capitalize';
  return (
    <Card className="max-w-2xl mx-auto" data-testid="apply-choose">
      <CardHeader><CardTitle className="text-lg">What would you like to study?</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <Field id="pr" label="Programme">
          <select id="pr" value={choice.programme_id} onChange={set('programme_id')}
                  className={selectClass} data-testid="apply-programme">
            <option value="">Choose a programme</option>
            {(prospectus.programmes || []).map((p) => (
              <option key={p.id} value={p.id}>{p.name} ({pretty(p.award)})</option>
            ))}
          </select>
        </Field>
        <Field id="in" label="Intake">
          <select id="in" value={choice.intake_id} onChange={set('intake_id')}
                  className={selectClass} data-testid="apply-intake">
            <option value="">Choose an intake</option>
            {(prospectus.intakes || []).map((i) => (
              <option key={i.id} value={i.id}>
                {i.name}{i.term_name ? ` · ${i.term_name}` : ''}
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field id="ty" label="I am applying as">
            <select id="ty" value={choice.applicant_type} onChange={set('applicant_type')}
                    className={selectClass} data-testid="apply-type">
              {['local', 'international', 'online', 'returning', 'transfer', 'sponsored']
                .map((t) => <option key={t} value={t}>{pretty(t)}</option>)}
            </select>
          </Field>
          <Field id="sm" label="Study mode">
            <select id="sm" value={choice.study_mode} onChange={set('study_mode')}
                    className={selectClass} data-testid="apply-mode">
              {(prospectus.study_modes || []).map((m) => (
                <option key={m} value={m}>{pretty(m)}</option>
              ))}
            </select>
          </Field>
          <Field id="at" label="Attendance">
            <select id="at" value={choice.attendance} onChange={set('attendance')}
                    className={selectClass} data-testid="apply-attendance">
              <option value="full_time">Full time</option>
              <option value="part_time">Part time</option>
            </select>
          </Field>
        </div>
        <Field id="src" label="How did you hear about us?">
          <select id="src" value={choice.source} onChange={set('source')}
                  className={selectClass} data-testid="apply-source">
            <option value="">Prefer not to say</option>
            {(prospectus.sources || []).map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
        </Field>
        <Button onClick={start} className="w-full" disabled={busy} data-testid="apply-start">
          {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Continue
        </Button>
      </CardContent>
    </Card>
  );
};

const ApplicationWorkspace = ({ token, application, reload }) => {
  const [form, setForm] = useState(application.form_data || {});
  const [ref, setRef] = useState({ referee_name: '', referee_email: '' });
  const [busy, setBusy] = useState('');
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const id = application.id;

  const save = async () => {
    setBusy('save');
    try {
      await call(`/admissions/portal/applications/${id}`,
        { method: 'PUT', body: { form_data: form }, token });
      toast.success('Saved — you can come back to this any time');
      reload();
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };

  const submit = async () => {
    setBusy('submit');
    try {
      const out = await call(`/admissions/portal/applications/${id}/submit`,
        { method: 'POST', token });
      toast.success(out.outstanding?.length
        ? `Submitted — we still need: ${out.outstanding.join(', ')}`
        : 'Submitted — our admissions team will be in touch');
      reload();
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };

  const upload = async (code, file) => {
    if (!file) return;
    setBusy(code);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await call(`/admissions/portal/applications/${id}/documents?requirement_code=${code}`,
        { method: 'POST', form: fd, token });
      toast.success('Uploaded');
      reload();
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };

  const askReference = async (item) => {
    if (!ref.referee_name || !ref.referee_email) {
      toast.error('Give us your referee\'s name and email');
      return;
    }
    setBusy(item.code);
    try {
      await call(`/admissions/portal/applications/${id}/references`, {
        method: 'POST', token,
        body: { reference_type: item.reference_type || 'personal',
                referee_name: ref.referee_name, referee_email: ref.referee_email,
                requirement_code: item.code },
      });
      toast.success('We have emailed them a private link');
      setRef({ referee_name: '', referee_email: '' });
      reload();
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };

  const done = ['verified', 'waived', 'paid', 'received'];
  const submitted = !['draft', 'in_progress'].includes(application.status);

  return (
    <div className="space-y-4 max-w-3xl mx-auto" data-testid="apply-workspace">
      <Card><CardContent className="p-4 flex items-center gap-3 flex-wrap">
        <div>
          <p className="text-xs text-slate-500">{application.application_number}</p>
          <p className="font-semibold text-slate-900">
            {application.programme_name || 'Your application'}
          </p>
          <p className="text-xs text-slate-500">
            {application.intake_name || 'No intake chosen yet'}
          </p>
        </div>
        <Badge className="ml-auto capitalize" data-testid="apply-status">
          {pretty(application.status)}
        </Badge>
      </CardContent></Card>

      <Card data-testid="apply-checklist">
        <CardHeader className="py-3"><CardTitle className="text-base">
          Your checklist · {application.checklist_summary?.satisfied || 0} of{' '}
          {application.checklist_summary?.required || 0} done
        </CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {(application.checklist || []).map((i) => (
            <div key={i.code} className="flex items-center gap-2 flex-wrap border-b last:border-0 pb-2"
                 data-testid={`apply-item-${i.code}`}>
              {done.includes(i.status)
                ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                : <span className="h-4 w-4 rounded-full border border-slate-300 shrink-0" />}
              <span className="text-sm flex-1">{i.label}</span>
              <Badge variant="outline" className="capitalize text-[10px]">{pretty(i.status)}</Badge>
              {i.kind === 'document' && (
                <label className="text-xs text-indigo-600 hover:underline cursor-pointer">
                  <Upload className="h-3 w-3 inline mr-0.5" />
                  {busy === i.code ? 'Uploading…' : 'Upload'}
                  <input type="file" className="hidden"
                         data-testid={`apply-upload-${i.code}`}
                         onChange={(e) => upload(i.code, e.target.files?.[0])} />
                </label>
              )}
              {i.kind === 'reference' && !done.includes(i.status) && i.status !== 'requested' && (
                <button onClick={() => askReference(i)} disabled={busy === i.code}
                        data-testid={`apply-reference-${i.code}`}
                        className="text-xs text-indigo-600 hover:underline">
                  <ShieldCheck className="h-3 w-3 inline mr-0.5" /> Ask for it
                </button>
              )}
            </div>
          ))}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
            <Input placeholder="Referee's name" value={ref.referee_name}
                   data-testid="apply-referee-name"
                   onChange={(e) => setRef({ ...ref, referee_name: e.target.value })} />
            <Input placeholder="Referee's email" type="email" value={ref.referee_email}
                   data-testid="apply-referee-email"
                   onChange={(e) => setRef({ ...ref, referee_email: e.target.value })} />
          </div>
          <p className="text-[11px] text-slate-500">
            Your referee answers on a private link — you'll see that it arrived, not what
            they wrote.
          </p>
        </CardContent>
      </Card>

      <Card data-testid="apply-form">
        <CardHeader className="py-3"><CardTitle className="text-base">About you</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field id="ch" label="Your church or assembly">
              <Input id="ch" value={form.church || ''} onChange={set('church')}
                     data-testid="apply-church" />
            </Field>
            <Field id="pa" label="Your pastor">
              <Input id="pa" value={form.pastor || ''} onChange={set('pastor')}
                     data-testid="apply-pastor" />
            </Field>
            <Field id="qu" label="Highest qualification">
              <Input id="qu" value={form.highest_qualification || ''}
                     onChange={set('highest_qualification')} data-testid="apply-qualification" />
            </Field>
            <Field id="sp" label="Who is funding your studies?">
              <Input id="sp" value={form.sponsor || ''} onChange={set('sponsor')}
                     placeholder="Myself, my church, my employer…" data-testid="apply-sponsor" />
            </Field>
          </div>
          <Field id="te" label="Your testimony">
            <Textarea id="te" rows={4} value={form.testimony || ''} onChange={set('testimony')}
                      placeholder="How you came to faith, and what led you here."
                      data-testid="apply-testimony" />
          </Field>
          <Field id="ca" label="Your calling and ministry goals">
            <Textarea id="ca" rows={3} value={form.calling || ''} onChange={set('calling')}
                      data-testid="apply-calling" />
          </Field>
          <div className="flex gap-2">
            <Button variant="outline" onClick={save} disabled={busy === 'save' || submitted}
                    data-testid="apply-save">
              {busy === 'save' ? 'Saving…' : 'Save and finish later'}
            </Button>
            <Button onClick={submit} disabled={busy === 'submit' || submitted}
                    data-testid="apply-final-submit">
              {busy === 'submit' ? 'Submitting…'
                : submitted ? 'Already submitted' : 'Submit my application'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};


const WALLET_HINTS = {
  ecocash: 'Approve the prompt on your EcoCash line',
  onemoney: 'Approve the prompt on your OneMoney line',
  innbucks: 'Open InnBucks and approve the request',
};

const FeeCard = ({ token, application, tenantId, reload }) => {
  const [fee, setFee] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [wallet, setWallet] = useState('');
  const [phone, setPhone] = useState('');
  const [pending, setPending] = useState(null);

  const load = React.useCallback(async () => {
    try {
      setFee(await call(`/admissions/portal/applications/${application.id}/fee`, { token }));
    } catch (e) { /* the school may not charge a fee */ }
    try {
      const r = await call(
        `/admissions/portal/applications/${application.id}/fee-wallets`, { token });
      setWallets(r.wallets || []);
    } catch (e) { setWallets([]); }
  }, [application.id, token]);

  useEffect(() => { load(); }, [load]);

  // Coming back from Stripe: confirm, credit and clear the query string.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const session = params.get('fee_session');
    if (params.get('fee_cancelled')) {
      toast.info('Payment cancelled — nothing was charged');
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    if (!session) return;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const r = await call(`/admissions/portal/fee-status/${session}`);
        if (r.payment_status === 'paid') {
          toast.success('Application fee paid — thank you');
          window.history.replaceState({}, '', window.location.pathname);
          await load();
          reload();
          return;
        }
      } catch (e) { /* keep trying */ }
      if (tries < 8) setTimeout(poll, 2000);
      else {
        toast.error('We could not confirm the payment yet — refresh in a minute');
        window.history.replaceState({}, '', window.location.pathname);
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A wallet payment finishes on the payer's phone, so we keep asking Paynow.
  useEffect(() => {
    if (!pending?.reference) return undefined;
    let stop = false;
    let tries = 0;
    const tick = async () => {
      if (stop) return;
      tries += 1;
      try {
        const r = await call(`/admissions/portal/fee-mobile-status/${pending.reference}`);
        if (r.paid) {
          toast.success('Application fee paid — thank you');
          setPending(null);
          await load();
          reload();
          return;
        }
        if (['cancelled', 'failed', 'disputed'].includes((r.status || '').toLowerCase())) {
          toast.error(`Payment ${r.status} — nothing was charged`);
          setPending(null);
          return;
        }
      } catch (e) { /* keep asking */ }
      if (tries < 36) setTimeout(tick, 5000);
      else {
        toast.info('Still waiting on the wallet — refresh this page in a minute');
        setPending(null);
      }
    };
    setTimeout(tick, 4000);
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending?.reference]);

  const payByCard = async () => {
    setBusy(true);
    try {
      const r = await call(`/admissions/portal/applications/${application.id}/fee-checkout`, {
        method: 'POST', token, body: { origin_url: window.location.origin },
      });
      window.location.href = r.url;
    } catch (e) { toast.error(e.message); setBusy(false); }
  };

  const payByWallet = async () => {
    setBusy(true);
    try {
      const r = await call(`/admissions/portal/applications/${application.id}/fee-mobile`, {
        method: 'POST', token,
        body: { method: wallet, phone, origin_url: window.location.origin },
      });
      setPending(r);
      toast.success(r.instructions);
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  if (!fee || !fee.fee) return null;
  const settled = fee.outstanding <= 0;

  return (
    <Card data-testid="apply-fee-card">
      <CardHeader className="py-3"><CardTitle className="text-base">Application fee</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-slate-600">
          <span className="font-semibold text-slate-900" data-testid="apply-fee-amount">
            {fee.currency} {fee.fee.toFixed(2)}
          </span>
          {settled ? (
            <span className="ml-2 text-emerald-600 font-medium" data-testid="apply-fee-paid">
              · paid, thank you
            </span>
          ) : (
            <span className="ml-2 text-slate-500">
              · {fee.currency} {fee.outstanding.toFixed(2)} outstanding
            </span>
          )}
        </div>

        {!settled && pending && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
               data-testid="apply-fee-pending">
            <p className="flex items-center gap-2 font-medium">
              <Loader2 className="h-4 w-4 animate-spin" /> Waiting for {pending.label}
            </p>
            <p className="mt-1 text-xs">{pending.instructions}</p>
            {pending.authorization_code && (
              <p className="mt-2 text-xs font-mono" data-testid="apply-fee-innbucks-code">
                Authorisation code: {pending.authorization_code}
              </p>
            )}
            <p className="mt-2 text-[11px] text-amber-700 font-mono">Ref {pending.reference}</p>
          </div>
        )}

        {!settled && !pending && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {fee.online_enabled && (
                <Button variant={wallet === '' ? 'default' : 'outline'} size="sm"
                        data-testid="apply-fee-method-card" onClick={() => setWallet('')}>
                  <CreditCard className="h-4 w-4 mr-2" /> Card
                </Button>
              )}
              {wallets.map((w) => (
                <Button key={w.key} size="sm"
                        variant={wallet === w.key ? 'default' : 'outline'}
                        data-testid={`apply-fee-method-${w.key}`}
                        onClick={() => setWallet(w.key)}>
                  <Smartphone className="h-4 w-4 mr-2" /> {w.label}
                </Button>
              ))}
            </div>

            {wallet ? (
              <div className="flex flex-wrap items-end gap-2">
                <div className="flex-1 min-w-0 sm:min-w-[200px]">
                  <label className="text-xs text-slate-500" htmlFor="fee-phone">
                    {WALLET_HINTS[wallet] || 'Wallet phone number'}
                  </label>
                  <Input id="fee-phone" value={phone} data-testid="apply-fee-phone"
                         onChange={(e) => setPhone(e.target.value)} placeholder="0771234567" />
                </div>
                <Button onClick={payByWallet} disabled={busy || phone.trim().length < 9}
                        data-testid="apply-fee-wallet-pay-btn">
                  {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Pay {fee.currency} {fee.outstanding.toFixed(2)}
                </Button>
              </div>
            ) : fee.online_enabled ? (
              <Button onClick={payByCard} disabled={busy} data-testid="apply-fee-pay-btn">
                {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                <CreditCard className="h-4 w-4 mr-2" /> Pay by card
              </Button>
            ) : (
              <p className="text-xs text-slate-500" data-testid="apply-fee-offline">
                Pay the bursar at the school office and they will record it here.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function ApplyPage() {
  const { tenantId } = useParams();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [prospectus, setProspectus] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadDashboard = React.useCallback(async () => {
    if (!token) { setDashboard(null); return; }
    try {
      setDashboard(await call('/admissions/portal/dashboard', { token }));
    } catch (e) {
      localStorage.removeItem(TOKEN_KEY);
      setToken('');
    }
  }, [token]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const p = await call(`/public/school/tenants/${tenantId}/prospectus`);
        if (alive) setProspectus(p);
      } catch (e) {
        toast.error(e.message);
      }
      await loadDashboard();
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [tenantId, loadDashboard]);

  const reload = async () => {
    const d = await call('/admissions/portal/dashboard', { token });
    setDashboard(d);
    if (d.application) {
      const full = await call(`/admissions/portal/applications/${d.application.id}`, { token });
      setDashboard({ ...d, application: { ...full, programme_name: d.programme_name,
        intake_name: d.intake_name } });
    }
  };

  if (loading) {
    return <div className="min-h-screen grid place-items-center">
      <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
    </div>;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="apply-page">
      <header className="bg-slate-900 text-white px-6 py-8">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] text-white/60">Admissions</p>
          <h1 className="text-3xl sm:text-4xl font-bold mt-1 flex items-center gap-3">
            <GraduationCap className="h-8 w-8" />
            {prospectus?.school?.name || 'Apply to study with us'}
          </h1>
          <p className="text-sm text-white/70 mt-2">
            Start today, save as you go, and come back whenever suits you.
          </p>
        </div>
      </header>

      <main className="px-6 py-8 pb-28">
        {!token ? (
          <AuthCard tenantId={tenantId} onIn={(t) => { setToken(t); }} />
        ) : !dashboard?.has_application ? (
          <ChooseProgramme tenantId={tenantId} token={token} prospectus={prospectus || {}}
                           onStarted={() => reload()} />
        ) : (
          <>
            <ApplicationWorkspace
              token={token} reload={reload}
              application={{ ...dashboard.application,
                programme_name: dashboard.programme_name,
                intake_name: dashboard.intake_name,
                checklist: dashboard.checklist }} />
            <div className="max-w-3xl mx-auto mt-4">
              <FeeCard token={token} tenantId={tenantId} reload={reload}
                       application={dashboard.application} />
            </div>
            <div className="max-w-3xl mx-auto mt-4 text-center">
              <button data-testid="apply-signout"
                      onClick={() => { localStorage.removeItem(TOKEN_KEY); setToken(''); }}
                      className="text-xs text-slate-500 hover:underline">Sign out</button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
